/**
 * Reactivation-population autotest resolver for the logged-in FocusedNav.
 *
 * Sister of AutotestFocusedNav (the activation/cold-non-premium resolver). This
 * one targets the FORMERLY-PREMIUM (churned) population: users who see the
 * "Reactivate Premium" CTA. Bucketed server-side in FocusedNavPresenter as
 * `navReactivationArm` (the nav is SSR-prerendered, so a client bucket would
 * flash) and resolved here to surface overrides on the reactivate CTA.
 *
 * The retired reactivation smoke validated churned ROUTING (server-side
 * bucketing) but not this resolver/delivery path, so this launches as an A/A:
 * REACT_ARMS is EMPTY, every arm resolves to CONTROL (byte-identical to today's
 * reactivate CTA), exercising the full delivery path at zero treatment. The
 * variants PR then populates REACT_ARMS with no relaunch/re-bucket. Test:
 * nav_reactivation_2026_q2_v1. The cohort is ~8x smaller than the trial CTA, so
 * the slate is intentionally small (3 arms) for power.
 */

// === Arms (control + 3) ===

export type ReactArm =
  | 'control'
  | 'v_react_discount' // lead with the real discount value
  | 'v_react_value_prop' // value-reminder copy
  | 'v_react_winback'; // warm winback copy

/** Resolved arm. `undefined` = not bucketed (premium / cold) → CONTROL. */
export type ReactResolvedArm = ReactArm | undefined;

// === Render-time inputs ===

export type ReactSurfaceArgs = {
  isMobileNav: boolean;
  /** The churned reactivation offer type, or null when there's no offer. */
  reactivateOffer: 'discount' | 'price' | null;
  /** The REAL formatted offer value (e.g. "50%", "$4.99"), or null. */
  reactivateValue: string | null;
};

// === Surfaces ===

export type ReactSurfaces = {
  /** Override copy for the churned "Reactivate Premium" CTA. `null` = today's
   * offer-derived text (CONTROL). */
  reactivateLabel: string | null;
};

/** CONTROL is the single source of every default — today's shipped reactivate CTA. */
export const REACT_CONTROL: ReactSurfaces = {
  reactivateLabel: null,
};

// === ARMS ===
// EMPTY for the A/A plumbing launch — every arm resolves to CONTROL, so the
// reactivate CTA renders byte-identically. The variants PR populates this with the
// three copy overrides (discount-emphasis, value-reminder, winback), keyed by the
// arm names above, with no relaunch.
type ReactArmSpec = (args: ReactSurfaceArgs) => Partial<ReactSurfaces>;

const REACT_ARMS: Partial<Record<ReactArm, ReactArmSpec>> = {};

/**
 * Resolve the reactivation surfaces for an arm. Unknown/undefined arm → CONTROL.
 * Always spreads over CONTROL so every field has a value.
 */
export function resolveReactivationSurfaces(
  arm: ReactResolvedArm,
  args: ReactSurfaceArgs,
): ReactSurfaces {
  const spec = arm !== undefined ? REACT_ARMS[arm] : undefined;
  return { ...REACT_CONTROL, ...(spec ? spec(args) : {}) };
}
